import { useCallback, useMemo, useRef, useState } from 'react';

import { OnChangeFn, SortingState } from '@tanstack/react-table';
import { ActivityIcon, AlertTriangleIcon, ZapIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { buildFilterBadges } from '@/components/ui/custom/ComplexFilter/buildFilterBadges';
import { ComplexFilterBar } from '@/components/ui/custom/ComplexFilter/ComplexFilterBar';
import {
  ComplexFilterCombobox,
  FilterDefinition,
} from '@/components/ui/custom/ComplexFilter/ComplexFilterCombobox';
import { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { ColumnVisibilityMenu } from '@/components/ui/table/column-visibility-menu';
import { DataTable } from '@/components/ui/table/data-table';
import { HiddenColumnsIndicator } from '@/components/ui/table/hidden-columns-indicator';
import { useColumnVisibility } from '@/components/ui/table/useColumnVisibility';
import { LocalDateFilter } from '@/modules/aiAgents/components/Overview/LocalDateFilter';
import { type DateSelection } from '@/modules/analytics/dateSelection';
import {
  RUN_STATUSES,
  RUN_STATUS_TRANSLATION_KEYS,
  type ScheduleCadence,
  type SimulationRun,
} from '@/modules/simulations/types';

import { columns } from './columns';

interface RunsTableProps {
  data: SimulationRun[];
  rowCount: number;
  isLoading: boolean;
  isFetchingNextPage: boolean;
  // Pagination
  fetchNextPage: () => void;
  hasNextPage: boolean;
  currentPageIndex: number;
  setCurrentPageIndex: (index: number) => void;
  // Sort
  sorting: SortingState;
  onSortingChange: OnChangeFn<SortingState>;
  // Filters
  filters: FilterItem[];
  activeFilterCount: number;
  setFilter: (key: string, values: string[], exclude?: boolean) => void;
  removeFilter: (key: string) => void;
  resetFilters: () => void;
  // Date selection (independent from `filters` — has its own UI control and storage)
  selection: DateSelection;
  onSelectionChange: (selection: DateSelection) => void;
  // Row helpers
  getCadence: (run: SimulationRun) => ScheduleCadence | undefined;
  onStop: (runId: string) => void;
  onDelete: (runId: string) => void;
}

export const RunsTable = ({
  data,
  rowCount,
  isLoading,
  isFetchingNextPage,
  fetchNextPage,
  hasNextPage,
  currentPageIndex,
  setCurrentPageIndex,
  sorting,
  onSortingChange,
  filters,
  activeFilterCount,
  setFilter,
  removeFilter,
  resetFilters,
  selection,
  onSelectionChange,
  getCadence,
  onStop,
  onDelete,
}: RunsTableProps) => {
  const { t } = useTranslation();
  const [openPillId, setOpenPillId] = useState<string | null>(null);

  const memoizedColumns = useMemo(
    () => columns({ onStop, onDelete, getCadence }),
    [onStop, onDelete, getCadence]
  );

  const [isCustomizeColumnsOpen, setIsCustomizeColumnsOpen] = useState(false);
  const customizeButtonRef = useRef<HTMLButtonElement>(null);
  const {
    columnVisibility,
    hideableColumns,
    setColumnVisibility,
    resetColumnVisibility,
    hasHiddenColumns,
  } = useColumnVisibility(memoizedColumns, 'simulationRunsColumnVisibility');

  const filterDefinitions = useMemo((): FilterDefinition[] => {
    const statusOptions = RUN_STATUSES.map((s) => ({
      label: t(RUN_STATUS_TRANSLATION_KEYS[s]),
      value: s,
    }));
    return [
      {
        id: 'status',
        label: t('simulations.filter_status'),
        icon: ActivityIcon,
        type: 'checkbox',
        options: statusOptions,
      },
      {
        id: 'fails',
        label: t('simulations.filter_fails'),
        icon: AlertTriangleIcon,
        type: 'radio',
        options: [
          { label: t('common.yes'), value: 'true' },
          { label: t('common.no'), value: 'false' },
        ],
      },
      {
        id: 'trigger',
        label: t('simulations.filter_trigger'),
        icon: ZapIcon,
        type: 'radio',
        options: [
          { label: t('simulations.manual'), value: 'manual' },
          { label: t('simulations.scheduled'), value: 'scheduled' },
        ],
      },
    ];
  }, [t]);

  const filterBadges = useMemo(
    () => buildFilterBadges(filterDefinitions, filters, { setFilter, removeFilter }),
    [filters, filterDefinitions, removeFilter, setFilter]
  );

  const getRowHref = useCallback((row: SimulationRun) => `../runs/${row.run_id}`, []);

  return (
    <div className="flex h-full min-h-0 flex-col gap-4">
      <div className="flex h-10 items-center justify-between gap-2 pl-1">
        <LocalDateFilter
          selection={selection}
          onSelectionChange={onSelectionChange}
          align="start"
          placeholder={t('simulations.filter_date')}
        />
        <div className="flex items-center gap-2">
          <ComplexFilterCombobox
            filterDefinitions={filterDefinitions}
            filters={filters}
            setFilter={setFilter}
            removeFilter={removeFilter}
            activeFilterCount={activeFilterCount}
            onOpenActivePill={setOpenPillId}
          />
          <ColumnVisibilityMenu
            columns={hideableColumns}
            columnVisibility={columnVisibility}
            onColumnVisibilityChange={setColumnVisibility}
            onReset={resetColumnVisibility}
            open={isCustomizeColumnsOpen}
            onOpenChange={setIsCustomizeColumnsOpen}
            onInteractOutside={(e) => {
              if (e.target === customizeButtonRef.current) {
                e.preventDefault();
                e.stopPropagation();
              }
            }}
          />
        </div>
      </div>
      <ComplexFilterBar
        filters={filterBadges}
        openPillId={openPillId}
        onOpenPillChange={setOpenPillId}
        onClearAll={resetFilters}
      />
      <DataTable.Root
        fill
        className="min-h-0 flex-1"
        columns={memoizedColumns}
        data={data}
        pagination
        manualSorting
        sorting={sorting}
        onSortingChange={onSortingChange}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        currentPageIndex={currentPageIndex}
        setCurrentPageIndex={setCurrentPageIndex}
        rowCount={rowCount}
        resourceName="simulation_run"
        emptyStateMessage={t('simulations.no_runs_found')}
        isLoading={isLoading}
        isFetchingNextPage={isFetchingNextPage}
        getRowId={(row: SimulationRun) => row.run_id}
        enableRowHover
        getRowHref={getRowHref}
        columnVisibility={columnVisibility}
        onColumnVisibilityChange={setColumnVisibility}
      >
        <DataTable.Content />
        <DataTable.Pagination
          centerContent={
            hasHiddenColumns ? (
              <HiddenColumnsIndicator
                onCustomize={() => setIsCustomizeColumnsOpen((prev) => !prev)}
                buttonRef={customizeButtonRef}
              />
            ) : undefined
          }
        />
      </DataTable.Root>
    </div>
  );
};
